import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import {
  Box,
  Drawer as MuiDrawer,
  AppBar as MuiAppBar,
  Toolbar,
  List,
  CssBaseline,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Avatar,
  CircularProgress,
  Collapse
} from '@mui/material';
import { Outlet, useNavigate } from "react-router-dom";
import {
  Menu as MenuIcon,
  Print,
  Settings,
  Assessment,
  PeopleAlt,
  PostAdd,
  Home,
  Apartment,
  ExpandLess,
  ExpandMore
} from '@mui/icons-material';

import logoblanco from '../imagenes/RFID_blanco.png';

const drawerWidth = 240;

/* ===================== STYLES ===================== */

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width'),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width'),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`
});

const DrawerHeader = styled('div')(({ theme }) => ({
  ...theme.mixins.toolbar,
  display: 'flex',
  justifyContent: 'flex-end',
}));

const AppBar = styled(MuiAppBar)(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin']),
  background: 'linear-gradient(90deg, #1976d2, #42a5f5)',
  marginLeft: open ? drawerWidth : `calc(${theme.spacing(7)} + 1px)`,
  width: open
    ? `calc(100% - ${drawerWidth}px)`
    : `calc(100% - (${theme.spacing(7)} + 1px))`,
}));

const Drawer = styled(MuiDrawer)(({ theme, open }) => ({
  width: drawerWidth,
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

/* ===================== COMPONENT ===================== */

export default function MiniDrawer() {
  const theme = useTheme();
  const navigate = useNavigate();

  const [open, setOpen] = React.useState(false);
  const [paramOpen, setParamOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [currentTime, setCurrentTime] = React.useState('');
  const [usuario, setUsuario] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  /* 🔐 VALIDACIÓN DE SESIÓN */
  React.useEffect(() => {
    const storedUser = localStorage.getItem('usuario');
    if (!storedUser || storedUser === "undefined") {
      setLoading(false);
      navigate('/login');
      return;
    }
    try {
      const parsed = JSON.parse(storedUser);
      setUsuario(parsed);
    } catch (error) {
      console.error("Error usuario:", error);
      localStorage.removeItem('usuario');
      navigate('/login');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  /* ⏰ reloj */
  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date().toLocaleTimeString());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const empresaId = usuario?.empresa_idempresa;

  const handleLogout = () => {
    localStorage.removeItem('usuario');
    navigate('/login');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />

      {/* 🔵 APPBAR */}
      <AppBar position="fixed" open={open}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <img
            src={usuario?.imagen ? usuario.imagen : logoblanco}
            alt="logo"
            style={{ height: 40, maxWidth: 200, objectFit: 'contain' }}
          />
          <Box display="flex" alignItems="center" gap={2}>
            <Typography>{currentTime}</Typography>
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
              <Avatar sx={{ bgcolor: '#1976d2' }}>
                {usuario?.nombre?.charAt(0)?.toUpperCase() || "U"}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
            >
              <MenuItem disabled>👤 {usuario?.nombre || "Usuario"}</MenuItem>
              <MenuItem onClick={handleLogout}>🚪 Cerrar sesión</MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* 🟣 DRAWER PRINCIPAL */}
      <Drawer variant="permanent" open={open}>
        <DrawerHeader>
          <IconButton onClick={() => setOpen(!open)}>
            <MenuIcon />
          </IconButton>
        </DrawerHeader>

        <Divider />

        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={() => navigate('/dashboard')}>
              <ListItemIcon><Home /></ListItemIcon>
              <ListItemText primary="Dashboard" />
            </ListItemButton>
          </ListItem>

          {String(empresaId) === "0" && (
            <ListItem disablePadding>
              <ListItemButton onClick={() => navigate('/empresas')}>
                <ListItemIcon><Apartment /></ListItemIcon>
                <ListItemText primary="Empresas" />
              </ListItemButton>
            </ListItem>
          )}

          <ListItem disablePadding>
            <ListItemButton onClick={() => navigate('/usuarios')}>
              <ListItemIcon><PeopleAlt /></ListItemIcon>
              <ListItemText primary="Usuarios" />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton onClick={() => navigate('/productos')}>
              <ListItemIcon><PostAdd /></ListItemIcon>
              <ListItemText primary="Productos" />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton onClick={() => navigate('/reportes')}>
              <ListItemIcon><Assessment /></ListItemIcon>
              <ListItemText primary="Reportes" />
            </ListItemButton>
          </ListItem>
        </List>

        <Divider />

        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={() => navigate('/impresion')}>
              <ListItemIcon><Print /></ListItemIcon>
              <ListItemText primary="Impresión" />
            </ListItemButton>
          </ListItem>

          {/* 🔹 BOTÓN PARA SUBMENÚ */}
          <ListItem disablePadding>
            <ListItemButton onClick={() => setParamOpen(!paramOpen)}>
              <ListItemIcon><Settings /></ListItemIcon>
              <ListItemText primary="Parámetros" />
              {paramOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
          </ListItem>

          {/* 🔹 SUBMENÚ DE PARÁMETROS */}
          <Collapse in={paramOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ pl: 4 }}>
              <ListItem disablePadding>
                <ListItemButton onClick={() => navigate('/ubicaciones')}>
                  <ListItemText primary="Ubicaciones" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => navigate('/estados')}>
                  <ListItemText primary="Estados" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => navigate('/descripcion')}>
                  <ListItemText primary="Descripciones" />
                </ListItemButton>
              </ListItem>
            </List>
          </Collapse>

        </List>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 10 }}>
        <Outlet />
      </Box>
    </Box>
  );
}